import PropTypes from "prop-types";
import { useContext, useEffect } from "react";

import { fireTrack } from "@swa-ui/analytics";
import { useDeviceInfo, useLocation, window } from "@swa-ui/browser";
import { swaDate } from "@swa-ui/date";
import { PlacementsContext } from "@swa-ui/placements";

import { ApplicationContext } from "../ApplicationContextProvider";
import { channels } from "../defines";
import { MktgDataContext } from "../MktgDataProvider";
import { usePageId } from "../usePageId";

const MESSAGE_DISPLAY_PLACEMENT_IDS = [
  "messageIncidentAlert",
  "messageIncidentAlertfit",
  "messageSystemAlert",
  "messageSystemAlertfit",
  "topBanner",
];

const TRAVEL_ADVISORY_PLACEMENT_IDS = ["messageTravelAdvisory", "messageTravelAdvisoryfit"];

export const AnalyticsDataUpdater = (props) => {
  const { additionalAnalyticsData, eventSpecificAnalyticsData, chaseAnalyticsData } = props;
  const { member_number, member_pointsbalance, member_status, user_loginstate, page_language } =
    additionalAnalyticsData ?? {};
  const { chase_acqsrccode, chase_highvalueindicator, chase_offeridentifier } =
    chaseAnalyticsData ?? {};
  const { channelId } = useContext(ApplicationContext);
  const { pageId } = usePageId();
  const { pathname } = useLocation();
  const {
    placements = {},
    placementsAnalytics = {},
    setPlacementsAnalytics = {},
  } = useContext(PlacementsContext) ?? {};
  const { screenSize } = useDeviceInfo();
  const { setMktgEventData, updateMktgGlobalData, updateAdobeGlobalData, sendAdobeEventData } =
    useContext(MktgDataContext);

  useEffect(() => {
    const { eventType = "", eventDescription = "" } = placementsAnalytics;

    if (placementsAnalytics.eventDescription && pathname.includes("vacations")) {
      const adobeAnalyticsObj = {
        event: "clickaction",
        eventinfo: {
          eventtype: eventType,
        },
        page: {
          eventdescription: eventDescription,
          pagename: generateSWAVPageName(),
          section: "marketing",
        },
      };

      sendAdobeEventData(adobeAnalyticsObj);
      setPlacementsAnalytics({});
    } else if (placementsAnalytics?.eventDescription) {
      fireTrack("squid", { page_description: eventDescription });
    }
  }, [placementsAnalytics]);

  useEffect(() => {
    updateMktgGlobalData({
      ...chaseAnalyticsData,
      chase_rr_number: "",
    });
  }, [chaseAnalyticsData]);

  useEffect(() => {
    if (pathname.includes("vacations")) {
      updateAdobeGlobalData({
        eventinfo: {
          eventtype: "other",
        },
        page: {
          pagename: generateSWAVPageName(),
          section: "marketing",
        },
      });
    }
  }, [pathname]);

  useEffect(() => {
    updateMktgGlobalData({
      devicetype: getDeviceType(),
      global_channelid: channelId,
      responsivesize: screenSize,
      ...getPageData(),
    });
    updateAdobeGlobalData({
      device: {
        devicetype: getDeviceType(),
        responsivesize: screenSize,
      },
    });
  }, [pathname, screenSize]);

  useEffect(() => {
    updateMktgGlobalData({
      ...additionalAnalyticsData,
    });
    updateAdobeGlobalData({
      customer: {
        authstatus: user_loginstate,
        chase_acqsrccode: chase_acqsrccode ? chase_acqsrccode : "",
        chase_highvalueindicator: chase_highvalueindicator ? "1" : "0",
        chase_offeridentifier: chase_offeridentifier ? chase_offeridentifier : "",
        membernumber: member_number,
        memberpointbalance: member_pointsbalance,
        membertierstatus: member_status,
      },
    });
  }, [additionalAnalyticsData]);

  useEffect(() => {
    setMktgEventData(eventSpecificAnalyticsData);
  }, [eventSpecificAnalyticsData]);

  useEffect(() => {
    const { contentBlockId, urgencyTrigger } = getAnalyticsContentData();
    const informationalMessageDisplayData = isInformationalMessageDisplayed()
      ? { informational_message_display: "1" }
      : {};
    const traveladvisory = isTravelAdvisoryDisplayed() ? { travel_advisory_displayed: "1" } : {};

    updateMktgGlobalData({
      contentblockid: contentBlockId,
      urgencytrigger: urgencyTrigger,
      ...informationalMessageDisplayData,
      ...traveladvisory,
    });
    updateAdobeGlobalData({
      content: {
        contentblockid: contentBlockId,
        language: page_language,
        traveladvisory: isTravelAdvisoryDisplayed() ? "1" : "",
      },
    });
  }, [placements]);

  return null;

  function getAnalyticsContentData() {
    const contentBlockIds = [];
    const urgencyTriggers = [];
    const wcmContentBlockId = Object.values(window.SWA?.wcm?.contentBlockId ?? {});

    Object.values(placements).forEach((placement) => {
      const { placementData = {} } = placement.content ?? {};
      const { contentBlockId } = placementData ?? {};

      contentBlockId && contentBlockIds.push(contentBlockId);

      if (placementData?.urgencyTrigger && contentBlockId) {
        const endDate = swaDate(placementData.endDate);
        const daysLeft = endDate.diff(swaDate(), "day");

        urgencyTriggers.push(`${contentBlockId};${daysLeft}`);
      }
    });

    return {
      contentBlockId: [...contentBlockIds, ...wcmContentBlockId].filter(Boolean).join(":") || null,
      urgencyTrigger: urgencyTriggers.join(),
    };
  }

  function getPageData() {
    let location = pathname;

    if (location === "/") {
      location = "/landing/home-page";
    }

    const names = location.split("/", 4).filter(Boolean);
    const page = pageId;
    const page_channel = names[0];
    const page_subchannel = names[1] || names[0];
    const page_name = names[2] || "index";

    return { page, page_channel, page_name, page_subchannel };
  }

  function getDeviceType() {
    let deviceType = "desktop";

    if (channelId === channels.mweb || channelId === channels.mwebCorp) {
      deviceType = "mobile";
    } else if (channelId === channels.android) {
      deviceType = channels.android;
    } else if (channelId === channels.ios) {
      deviceType = channels.ios;
    }

    return deviceType;
  }

  function isInformationalMessageDisplayed() {
    const placementIds = Object.keys(placements);

    return placementIds.some((placementId) => MESSAGE_DISPLAY_PLACEMENT_IDS.includes(placementId));
  }

  function isTravelAdvisoryDisplayed() {
    const placementIds = Object.keys(placements);

    return placementIds.some((placementId) => TRAVEL_ADVISORY_PLACEMENT_IDS.includes(placementId));
  }

  function retrievePageType() {
    return window?.document?.getElementById("swav-page-type")?.innerText || "";
  }

  function retrievePageDetail() {
    return window?.document?.getElementById("swav-page-detail")?.innerText || "";
  }

  function generateSWAVPageName() {
    return `swav ${retrievePageType()}:${retrievePageDetail()}`;
  }
};

AnalyticsDataUpdater.propTypes = {
  additionalAnalyticsData: PropTypes.object,
  chaseAnalyticsData: PropTypes.object,
  eventSpecificAnalyticsData: PropTypes.object,
};
